import { useState, useEffect, useCallback } from 'react';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { LayoutDashboard, BookMarked, AlertTriangle, ClipboardCheck, Building2, ShieldCheck, FileText, AlertCircle, Briefcase } from 'lucide-react';
import { practiceComplianceAPI as API } from '../lib/practiceComplianceAPI';
import { PMOverviewTab } from '../components/compliance/PMOverviewTab';
import { PMManualsTab } from '../components/compliance/PMManualsTab';
import { PMRiskRegisterTab } from '../components/compliance/PMRiskRegisterTab';
import { PMAcknowledgementsTab } from '../components/compliance/PMAcknowledgementsTab';
import { PMProfileTab } from '../components/compliance/PMProfileTab';
import { PMInsuranceTab } from '../components/compliance/PMInsuranceTab';
import { QMSMetricsCard } from '../components/compliance/QMSMetricsCard';
import QMS from './QMS';

// Old /qms deep links (legacy QMS tab names) map onto the merged page.
const ALIASES = { dashboard: 'overview', cpd: 'overview', 'ai-policy': 'policies', engagements: 'engagement-risk' };
const initialTab = () => { const t = new URLSearchParams(window.location.search).get('tab') || 'overview'; return ALIASES[t] || t; };

export default function PracticeManuals() {
  const [overview, setOverview] = useState(null);
  const [tab, setTab] = useState(initialTab);

  const loadOverview = useCallback(async () => {
    try {
      const res = await API.overview();
      setOverview(res.data);
    } catch { /* non-fatal */ }
  }, []);

  useEffect(() => { loadOverview(); }, [loadOverview]);
  const canManage = !!overview?.can_manage;

  return (
    <div className="p-6 max-w-6xl mx-auto space-y-6" data-testid="practice-manuals-page">
      <div>
        <h1 className="text-3xl font-bold text-foreground" style={{ fontFamily: 'Plus Jakarta Sans, sans-serif' }} data-testid="qms-page-heading">QMS</h1>
        <p className="text-muted-foreground mt-1">
          Quality management: practice manuals (APES 320 / APES 325 / conditional ASQM 1), policies, risk registers, incidents and staff acknowledgements.
        </p>
      </div>

      <Tabs value={tab} onValueChange={setTab}>
        <TabsList className="flex-wrap h-auto">
          <TabsTrigger value="overview" data-testid="pm-tab-overview"><LayoutDashboard className="w-4 h-4 mr-2" /> Overview</TabsTrigger>
          <TabsTrigger value="manuals" data-testid="pm-tab-manuals"><BookMarked className="w-4 h-4 mr-2" /> Practice Manuals</TabsTrigger>
          <TabsTrigger value="policies" data-testid="qms-tab-policies"><FileText className="w-4 h-4 mr-2" /> Policies</TabsTrigger>
          <TabsTrigger value="risks" data-testid="pm-tab-risks"><AlertTriangle className="w-4 h-4 mr-2" /> Risk Register (firm)</TabsTrigger>
          <TabsTrigger value="engagement-risk" data-testid="qms-tab-engagement-risk"><Briefcase className="w-4 h-4 mr-2" /> Engagement Risk</TabsTrigger>
          <TabsTrigger value="incidents" data-testid="qms-tab-incidents"><AlertCircle className="w-4 h-4 mr-2" /> Incidents</TabsTrigger>
          {canManage && <TabsTrigger value="acks" data-testid="pm-tab-acks"><ClipboardCheck className="w-4 h-4 mr-2" /> Acknowledgements</TabsTrigger>}
          {canManage && <TabsTrigger value="profile" data-testid="pm-tab-profile"><Building2 className="w-4 h-4 mr-2" /> Practice Profile</TabsTrigger>}
          {canManage && <TabsTrigger value="insurance" data-testid="pm-tab-insurance"><ShieldCheck className="w-4 h-4 mr-2" /> Insurance</TabsTrigger>}
        </TabsList>

        <TabsContent value="overview" className="space-y-6">
          <QMSMetricsCard onNavigate={setTab} />
          <PMOverviewTab overview={overview} onNavigate={setTab} onRefresh={loadOverview} />
        </TabsContent>
        <TabsContent value="manuals"><PMManualsTab onChanged={loadOverview} /></TabsContent>
        <TabsContent value="policies" className="space-y-6">
          <QMS section="policies" />
          <QMS section="ai-policy" />
        </TabsContent>
        <TabsContent value="risks"><PMRiskRegisterTab onChanged={loadOverview} /></TabsContent>
        <TabsContent value="engagement-risk"><QMS section="engagements" /></TabsContent>
        <TabsContent value="incidents"><QMS section="incidents" /></TabsContent>
        {canManage && <TabsContent value="acks"><PMAcknowledgementsTab /></TabsContent>}
        {canManage && <TabsContent value="profile"><PMProfileTab onSaved={loadOverview} /></TabsContent>}
        {canManage && <TabsContent value="insurance"><PMInsuranceTab /></TabsContent>}
      </Tabs>
    </div>
  );
}
